import {
  DocumentPdfPane,
  type DocumentPaneRenderProps,
} from '@aecom-ai-engineering/react-components';
import { HTMLViewer, IfcViewer } from '@consigli/facade';
import { type ReactNode } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

function ImagePane({ active, src, fileName }: DocumentPaneRenderProps) {
  if (!active) return null;
  const url = typeof src === 'string' ? src : src instanceof URL ? src.href : undefined;
  if (!url) return null;

  return (
    <div className="flex h-full w-full items-center justify-center bg-white">
      <TransformWrapper wheel={{ smoothStep: 0.01 }} centerOnInit>
        <TransformComponent wrapperStyle={{ height: '100%', width: '100%' }}>
          <img src={url} alt={fileName ?? 'Image'} className="max-h-full max-w-full" />
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
}

const documentViewerSpinner = (
  <div data-ds-document-viewer-status="">
    <span aria-hidden="true" data-ds-document-viewer-spinner="" />
    Loading...
  </div>
);

function IfcPane({ active, src }: DocumentPaneRenderProps) {
  if (!active) return null;
  const url = typeof src === 'string' ? src : src instanceof URL ? src.href : undefined;
  if (!url) return null;

  return (
    <div className="h-full w-full">
      <IfcViewer fileUrl={url} loadingElement={documentViewerSpinner} />
    </div>
  );
}

function HtmlPane({ active, src }: DocumentPaneRenderProps) {
  if (!active) return null;
  const url = typeof src === 'string' ? src : src instanceof URL ? src.href : undefined;
  if (!url) return null;

  return (
    <div className="h-full w-full bg-white">
      <HTMLViewer fileUrl={url} />
    </div>
  );
}

function PdfPane(props: DocumentPaneRenderProps) {
  return (
    <DocumentPdfPane
      active={props.active}
      caption={props.caption}
      onSidebarContextChange={props.onSidebarContextChange}
      src={props.src}
      continuousScroll
    />
  );
}

/**
 * Build the `renderers` dictionary for backoffice `<DocumentViewer>` instances.
 * Covers PDF (with continuous scroll), images (zoom/pan), HTML (read-only
 * iframe), and IFC (3D). The results tab's editable HTML editor is handled
 * separately and does not use this renderer.
 */
export function createBackofficeRenderers() {
  return {
    pdf: PdfPane,

    jpg: ImagePane,
    jpeg: ImagePane,
    png: ImagePane,
    gif: ImagePane,
    webp: ImagePane,

    html: HtmlPane,
    htm: HtmlPane,

    ifc: IfcPane,
  } satisfies Record<string, (props: DocumentPaneRenderProps) => ReactNode>;
}
